import { useState } from "react";
import { KeyRound, Mail, Lock, Unlock, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Small pencil-icon button + modal that lets the Founder change a user's
 * login email and/or password in one shot. Also exposes the emergency
 * lock/unlock toggle. Wired to `/admin/users/{uid}/credentials` +
 * `/admin/users/{uid}/lock` + `/admin/users/{uid}/unlock`.
 *
 * `user` shape: {id, name, email, locked?}
 * `onDone` optional callback fired after a successful save.
 */
export default function CredentialEditorButton({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user.email || "");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const payload = {};
    if (email && email.trim().toLowerCase() !== (user.email || "").toLowerCase()) payload.new_email = email.trim();
    if (pwd && pwd.length >= 6) payload.new_password = pwd;
    if (!Object.keys(payload).length) { toast.error("Nothing changed — enter a new email or password"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/credentials`, payload);
      toast.success("Credentials updated");
      setOpen(false); setPwd("");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleLock = async () => {
    if (!window.confirm(user.locked ? `Unlock ${user.name}?` : `Emergency-lock ${user.name}? Their password will be scrambled and all sessions invalidated.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/${user.locked ? "unlock" : "lock"}`);
      toast.success(user.locked ? "Account unlocked" : "Account locked");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid={`cred-edit-${user.id}`} onClick={() => setOpen(true)}
        title="Change login email / password (Founder only)"
        className="text-[color:var(--aii-muted)] hover:text-brand p-1">
        <KeyRound size={14}/>
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid={`cred-modal-${user.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><KeyRound size={10}/> Change credentials</div>
                <h3 className="font-display font-semibold">{user.name}</h3>
              </div>
              <button onClick={() => setOpen(false)}><X size={16}/></button>
            </div>
            {user.locked && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded p-2 mb-3">
                🔒 This account is currently locked. Unlock it below before setting a new password.
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold flex items-center gap-1"><Mail size={10}/> Login email</label>
                <input data-testid={`cred-email-${user.id}`} type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold flex items-center gap-1"><KeyRound size={10}/> New password <span className="text-[color:var(--aii-muted)] font-normal">(leave blank to keep current)</span></label>
                <input data-testid={`cred-pwd-${user.id}`} type="text" value={pwd} onChange={e => setPwd(e.target.value)}
                  placeholder="Min 6 characters" className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm font-mono"/>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button data-testid={`cred-lock-${user.id}`} onClick={toggleLock} disabled={busy}
                className={`text-xs px-3 py-1.5 rounded font-semibold flex items-center gap-1 disabled:opacity-50 ${user.locked ? "border border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border border-red-300 text-red-700 hover:bg-red-50"}`}>
                {user.locked ? <><Unlock size={11}/> Unlock account</> : <><Lock size={11}/> Emergency lock</>}
              </button>
              <button data-testid={`cred-save-${user.id}`} onClick={save} disabled={busy}
                className="text-sm px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold disabled:opacity-50">
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
